import type { ParameterTimeSeriesConfiguration } from "@/lib/api";
import type { TimeSeriesParameterOption } from "@/features/shared/widgets/map/time-series/time-series-parameter.types";
import { resolveFetchUnitForParameter } from "@/lib/parameters/parameter-units.utils";
import { WindDirectionMarkerValue } from "@/features/shared/widgets/map/wind-direction-marker-value";

import {
  formatMarkerBubblePreviewText,
  isMarkerBubblePreviewWindDirection,
  markerBubblePreviewWindDirectionDegrees,
} from "./marker-bubble-preview.utils";
import styles from "./marker-bubble-preview.module.css";

type MarkerBubblePreviewProps = {
  parameters: TimeSeriesParameterOption[];
  parameterIds: string[];
  timeSeriesConfigurations: ParameterTimeSeriesConfiguration[];
  /** When set, shows station name instead of parameter values (no display groups). */
  stationName?: string;
  size?: "default" | "large";
};

/** Prototype marker bubble: rounded body + bottom-center notch, values separated by short dividers. */
export function MarkerBubblePreview({
  parameters,
  parameterIds,
  timeSeriesConfigurations,
  stationName,
  size = "default",
}: MarkerBubblePreviewProps) {
  if (stationName?.trim()) {
    return (
      <div className={styles.wrapper} data-size={size}>
        <div className={styles.bubble}>
          <span className={styles.value}>{stationName.trim()}</span>
        </div>
      </div>
    );
  }

  const resolved = parameterIds
    .map((id) => parameters.find((p) => p.id === id))
    .filter((p): p is TimeSeriesParameterOption => p !== undefined);

  if (resolved.length === 0) {
    return (
      <div className={styles.wrapper} data-size={size}>
        <div className={styles.bubble}>
          <span className={styles.value}>—</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper} data-size={size}>
      <div className={styles.bubble} data-multiple={resolved.length > 1 || undefined}>
        {resolved.map((param, index) => {
          const unitCode: string | undefined = resolveFetchUnitForParameter(
            param.id,
            timeSeriesConfigurations,
          );
          const text = formatMarkerBubblePreviewText(param, unitCode);
          const content = isMarkerBubblePreviewWindDirection(param, unitCode) ? (
            <WindDirectionMarkerValue
              degrees={markerBubblePreviewWindDirectionDegrees(param, unitCode)}
              label={text}
            />
          ) : (
            text
          );
          return (
            <span
              key={param.id}
              className={styles.value}
              data-inverted={(resolved.length > 1 && index === 0) || undefined}
            >
              {content}
            </span>
          );
        })}
      </div>
    </div>
  );
}
